import { useEffect, useState } from 'react';

import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';

import APIMediaServer from '../api/APIMediaServer';
import DocumentPage from '../components/pdf/DocumentPage';
import { BusinessInterface, ProductInterface } from '../interfaces/ServerInterfaces';
import { blobToDataURL } from './helpers/commons';
import defaultLogo from '@/assets/image-default.jpg'

interface CookBookPdfInterface {
  products: ProductInterface[]
  businessName: string
  business: BusinessInterface | null
}

//Styles
const tableStyles = StyleSheet.create({
  businessLogo: {
    height: 85.4,
    objectFit: 'contain',
    paddingLeft: 0.9,
    borderRadius: '50%',
    marginTop: 10,
  },
  tableTitle: {
    marginTop: 10,
    fontSize: 13,
    fontWeight: 'semibold',
    textDecoration: 'underline',
  },
  table: {
    marginTop: 5,
    paddingBottom: 10,
    gap: 4,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  productCard: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: '100%',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    borderBottom: 'solid',
    borderBottomColor: '#7a7a7a',
    borderBottomWidth: 1,
  },
  rowHeader: {
    // borderBottom: "solid",
    // borderBottomColor: "#9ca3af",
    // borderBottomWidth: 2,
    backgroundColor: '#000000',
    color: '#ffffff',
    fontWeight: 500,
  },
  normalText: {
    fontSize: 12,
    fontWeight: 500,
  },
  bodyText: {
    fontSize: 6,
  },

  productImage: {
    height: 150,
    width: 150,
    objectFit: 'contain',
    paddingLeft: 1.8,
    borderRadius: '20%',
  },
})

const CookBookPdf = ({
  products,
  businessName,
  business,
}: CookBookPdfInterface) => {
  // State for PRODUCT images
  const [productImageSources, setProductImageSources] = useState<{
    [key: number]: string
  }>({})
  // State for BUSINESS logo
  const [businessLogoSrc, setBusinessLogoSrc] = useState<string>(
    defaultLogo
  )

  // Generic function to fetch image data
  const fetchImageData = async (imageId: number): Promise<string> => {
    try {
      const response = await APIMediaServer.get('/files/' + imageId, {
        responseType: 'blob',
      })
      return (await blobToDataURL(response.data)) as string
    } catch (error) {
      console.error(`Error fetching image ${imageId}:`, error)
      return defaultLogo // Return default path on error
    }
  }

  // Effect for fetching PRODUCT images
  useEffect(() => {
    const fetchAllProductImages = async () => {
      const imagePromises: Promise<{ id: number; dataUrl: string } | null>[] =
        []
      const uniqueImageIds = new Set<number>()

      products.forEach((prod) => {
        const imageId = prod.images?.[0]?.id
        // Fetch only if ID exists and not already fetched
        if (imageId && !productImageSources[imageId]) {
          uniqueImageIds.add(imageId)
        }
      })

      if (uniqueImageIds.size === 0) {
        return
      }

      uniqueImageIds.forEach((imageId) => {
        imagePromises.push(
          fetchImageData(imageId) // Use renamed function
            .then((dataUrl) => ({ id: imageId, dataUrl }))
            .catch((error) => {
              console.error(
                `Failed to process product image ${imageId}:`,
                error
              )
              return null
            })
        )
      })

      const results = await Promise.allSettled(imagePromises)

      const newSources: { [key: number]: string } = {}
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          newSources[result.value.id] = result.value.dataUrl
        }
      })

      // Update PRODUCT image sources state
      setProductImageSources((prevSources) => ({
        ...prevSources,
        ...newSources,
      }))
    }

    fetchAllProductImages()
  }, [products, productImageSources]) // Depend on productImageSources to avoid re-fetching already loaded images

  // Effect for fetching BUSINESS logo
  useEffect(() => {
    const fetchBusinessLogo = async () => {
      const logoId = business?.logo?.id
      if (logoId) {
        try {
          const dataUrl = await fetchImageData(logoId)
          setBusinessLogoSrc(dataUrl)
        } catch (error) {
          console.log(error)
        }
      }
    }

    fetchBusinessLogo()
  }, [business?.logo?.id]) // Depend only on the logo ID

  const groupedNames = products.reduce<{ [key: string]: ProductInterface[] }>(
    (acc, item) => {
      const firstLetter = item.name[0].toUpperCase()
      if (!acc[firstLetter]) {
        acc[firstLetter] = []
      }
      acc[firstLetter].push(item)
      return acc
    },
    {}
  )

  return (
    <DocumentPage>
      {/* Business Logo Section */}
      <View
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          marginTop: 200,
          textAlign: 'center',
          fontSize: 20,
          fontWeight: 700,
          gap: 30,
        }}
        wrap={false}
      >
        <Image
          style={tableStyles.businessLogo}
          // Use the new state for business logo, provide fallback
          src={businessLogoSrc}
        />

        <Text>{businessName}</Text>
        <Text style={{ fontSize: 30, fontWeight: 800 }}> RECETARIO</Text>
      </View>

      {/* Products Section */}
      <View style={tableStyles.table} break>
        {Object.keys(groupedNames).map((letter) => (
          <div key={letter}>
            <Text style={{ textAlign: 'left' }}>{letter}</Text>
            {groupedNames[letter].map((prod, idx) => {
              // Get product image source from state
              const productImageId = prod.images?.[0]?.id
              const productSrc =
                productImageId && productImageSources[productImageId]
                  ? productImageSources[productImageId]
                  : defaultLogo

              return (
                <View
                  key={idx}
                  style={{
                    width: '100%',
                    padding: 2,
                    marginTop: 10,
                  }}
                >
                  {/* Nombre */}
                  <Text
                    style={{
                      textAlign: 'left',
                      paddingBottom: 1,
                      fontSize: 16,
                      fontWeight: 700,
                    }}
                  >
                    {prod.name}
                  </Text>

                  <View
                    style={{
                      ...tableStyles.productCard,
                    }}
                  >
                    <div>
                      {/* Imagen */}
                      <View style={{ width: '30%' }}>
                        <Image
                          style={tableStyles.productImage}
                          // Use the product image source from state
                          src={productSrc}
                        />
                      </View>
                    </div>

                    <div>
                      {/* Ingredientes del producto  */}
                      <View
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-start',
                          alignContent: 'flex-start',
                          marginLeft: 25,
                          marginTop: 8,
                        }}
                      >
                        {/* Descripción */}
                        {!!prod.description && (
                          <View
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              width: 400,
                              flexWrap: 'nowrap',
                            }}
                          >
                            <View>
                              <Text
                                style={{
                                  ...tableStyles.normalText,
                                  textAlign: 'left',
                                }}
                              >
                                Descripción:
                                {'          ' + (prod.description ?? '')}
                              </Text>
                            </View>
                          </View>
                        )}
                      </View>
                    </div>
                  </View>

                  {/* Nombre */}
                  <Text
                    style={{
                      textAlign: 'left',
                      paddingBottom: 2,
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    Pasos de elaboración:
                  </Text>

                  {/* Nombre */}
                  <Text
                    style={{
                      textAlign: 'left',
                      paddingBottom: 4,
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {prod.elaborationSteps ?? '-'}
                  </Text>
                </View>
              )
            })}
          </div>
        ))}
      </View>
    </DocumentPage>
  )
}

export default CookBookPdf
